import { useContext, useMemo, useState } from 'react'

import { Input } from 'antd'
import { useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { faCalendar, faFileExport, faPlus } from '@fortawesome/pro-solid-svg-icons'

import { GlobalContext } from '../../../Providers/GlobalProvider'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { isAdminReseau, isClinicalSecretary, isSchoolMaccsUser } from '../../../utils/roles'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { getLanguageByLocale, isNonEmptyArray, isObject } from '../../../utils'
import MaccsInternshipFilters from './MaccsInternshipFilters'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { Routes } from '../../../routes'
import TooltipIconButton from '../../../Components/antd/Buttons/TooltipIconButton'
import { MaccsInternshipModalContext } from '../../../Providers/Forms/MaccsInternshipModal/MaccsInternshipModal'
import FormButton from '../../../Components/antd/Buttons/FormButton'
import { MaccsInternship } from '../../../utils/entities/maccsInternship'
import PaginationHeader from '../../../Components/shared/InternshipsManager/PaginationHeader'
import WeekNavigator from '../../../Components/shared/InternshipsManager/WeekNavigator'
import { DATE_FORMAT_SHOWN } from '../../../Components/shared/InternshipsManager/utils/constants'
import CurrentWeekButton from '../../../Components/shared/InternshipsManager/CurrentWeekButton'
import InternshipsManagerActionsHeaderExportModal from '../../../Components/shared/InternshipsManagerActionsHeaderExportModal'
import { exportTimeline } from '../../../utils/api/maccsInternship'
import { getDocument } from '../../../utils/api/export'
import { onError } from '../../../utils/apiHelper'

const Search = Input.Search

const Header = ({ history, loading, metadata, onPageChange, onResetFilters, refresh }) => {
  const { setCurrentDate } = useContext(GlobalContext)
  const { internshipDate, internshipFilters, internshipSearch, setInternshipDate, setInternshipSearch, setInternshipFilters } = useContext(GlobalFiltersContext)
  const { institutions, order, schools, states } = internshipFilters

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [exportLoading, setExportLoading] = useState(false)
  const [displayExportModal, setDisplayExportModal] = useState(false)

  const buttonsConfig = useMemo(() => ({
    shouldDisplayAddButton: isAdminReseau(user) || isClinicalSecretary(user),
    scheduleButtonLabel: 'internships_header.schedule_button.edit'
  }), [user])

  const handleExportTimeline = (startDate, endDate, checked, filter) => {
    setDisplayExportModal(false)
    setExportLoading(true)

    const body = {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      language: getLanguageByLocale(user.language)
    }

    if (checked) {
      Object.keys(filter).forEach(key => {
        if (filter[key]) {
          if (typeof body.filter === 'undefined') {
            body.filter = {}
          }

          body.filter[key] = filter[key]
        }
      })
    }

    exportTimeline(user, body).then(json => {
      getDocument(
        json.job,
        user,
        () => onError(t('spreadsheet_download.error'))
      )
    }).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }

  return (
    <div className='internships-manager-header'>
      <div className='actions-and-filters'>
        <Search
          value={internshipSearch}
          placeholder={t('Filter internships')}
          onChange={e => { setInternshipSearch(e.target.value) }}
          style={{ width: 175 }}
        />
        <div className='h-spacing' />
        <MaccsInternshipFilters onFilterChange={setInternshipFilters} onResetFilters={onResetFilters} />

        <div className='flex-fill' />
        <IconButton
          type='primary'
          icon={faCalendar}
          disabled={loading}
          ghost
          onClick={() => {
            setCurrentDate(internshipDate)
            history.push(Routes.SHIFTS_MANAGER)
          }}
          text={t(buttonsConfig.scheduleButtonLabel)}
        />
        {isSchoolMaccsUser(user) && (
          <>
            <div className='h-spacing' />
            <FormButton
              context={MaccsInternshipModalContext}
              icon={faPlus}
              text={t('internships_header.add_internship_button.label')}
              type='primary'
              disabled={loading}
              onClick={setter => setter({
                object: new MaccsInternship({ institution: isNonEmptyArray(user.institutions) ? user.institutions[0] : null, school: isObject(user.school) ? user.school : null }),
                callback: refresh
              })}
              ghost
            />
          </>
        )}
      </div>
      <div className='table-modifier-buttons'>
        <PaginationHeader
          pageIndex={metadata.page}
          maxPageIndex={metadata.totalPages}
          dataType='Internship'
          dataCount={metadata.total}
          onPageChange={onPageChange}
        />
        <div className='flex-fill' />
        <WeekNavigator
          loading={loading}
          date={internshipDate}
          dateFormat={DATE_FORMAT_SHOWN}
          navigationPeriod='month'
          onDateChange={setInternshipDate}
        />
        <div className='h-spacing' />
        <CurrentWeekButton
          date={internshipDate}
          loading={loading}
          title={t('Go back to current week')}
          onClick={setInternshipDate}
        />
        <div className='h-spacing' />
        <TooltipIconButton
          title={t('internships_header.export_timeline_button')}
          icon={faFileExport}
          onClick={() => setDisplayExportModal(true)}
          tooltipProps={{ placement: 'top' }}
          buttonProps={{ loading: exportLoading }}
        />
      </div>
      {displayExportModal && (
        <InternshipsManagerActionsHeaderExportModal
          startDate={internshipDate}
          weeksShownCount={48}
          filterSearch={internshipSearch}
          filterStates={states}
          filterSchools={schools}
          filterInstitutions={institutions}
          orderBy={order.by}
          orderDirection={order.direction}
          onCancel={() => setDisplayExportModal(false)}
          onExport={handleExportTimeline}
          visible
        />
      )}
    </div>
  )
}

export default withRouter(Header)
